import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Divider } from 'primereact/divider';
import { Steps } from 'primereact/steps';
import { Panel } from 'primereact/panel';
// next/image removido para compatibilidad con React
import { useTheme } from '../hooks/useTheme';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';


export default function CambiosDevoluciones() {
    const [activeIndex, setActiveIndex] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const { theme, toggleTheme } = useTheme();

    // Actualización de pasos según política vigente
    const processSteps = [
        {
            label: 'Contacto',
            icon: 'pi pi-envelope',
            description: 'Inicia tu solicitud dentro de 7 días calendario desde la entrega.'
        },
        {
            label: 'Evaluación técnica',
            icon: 'pi pi-search',
            description: 'Nuestro equipo técnico verifica el producto y su garantía.'
        },
        {
            label: 'Dictamen',
            icon: 'pi pi-file',
            description: 'Emitimos un informe técnico confirmando o descartando defecto de fábrica.'
        },
        {
            label: 'Resolución',
            icon: 'pi pi-check-circle',
            description: 'Si se confirma defecto: reposición del mismo modelo (sujeto a stock) o nota de crédito. No se aceptan cambios por otros motivos.'
        }
    ];

    // Aspectos clave actualizados
    const policyHighlights = [
        {
            title: 'Devoluciones por defectos',
            description: 'Aceptamos devoluciones solo por productos defectuosos confirmados por nuestro departamento técnico.',
            icon: 'pi pi-shield',
            severity: 'warning'
        },
        {
            title: 'Cambios no aceptados',
            description: 'No se aceptan cambios por preferencia, talla, color u otros motivos no relacionados a defectos.',
            icon: 'pi pi-ban',
            severity: 'info'
        },
        {
            title: 'Plazos y proceso',
            description: 'Solicita evaluación dentro de 7 días calendario. El dictamen técnico se emite en hasta 5 días hábiles.',
            icon: 'pi pi-clock',
            severity: 'success'
        }
    ];

    return (
        <div className={'landing-page theme-' + theme}>
            <Header theme={theme} onToggleTheme={toggleTheme} />
            <div className="surface-ground min-h-screen">
                {/* Hero Section */}
                <div className="surface-section py-2 lg:py-3">
                    <div className=" mx-auto px-4">
                        <div className="grid">
                            <div className="col-12 lg:col-8">
                                <h1 className="text-4xl lg:text-6xl font-bold text-primary mb-4 line-height-2">
                                    Política de Cambios y Devoluciones
                                </h1>
                                <p className="text-xl lg:text-2xl text-700 line-height-3 mb-6">
                                    En Laptops.pe queremos que tengas total confianza al realizar tu compra.
                                    Estas políticas están alineadas con lo configurado en Google Merchant y con la legislación peruana.
                                </p>
                                <div className="flex flex-wrap gap-3">
                                    {/* Resumen superior actualizado */}
                                    <Badge value="Devoluciones por defecto" severity="warning" className="" size="large" />
                                    <Badge value="Cambios no aceptados" severity="info" className="" size="large" />
                                    <Badge value="Evaluación técnica previa" severity="success" className="" size="large" />
                                </div>
                            </div>
                            <div className="col-12 lg:col-4 text-center">
                                <img
                                    src="/imagenes/devoluciones.jpeg"
                                    alt="Cambios y devoluciones"
                                    width="400"
                                    height="400"
                                    className="rounded-xl"
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className=" mx-auto px-4 py-6">
                {/* Aspectos destacados de la política */}
                <div className="mb-6">
                    <Card>
                        <div className="p-0 text-center">
                            <h2 className="text-4xl font-bold text-primary">Aspectos clave de nuestra política</h2>
                            <div className="grid">
                                {policyHighlights.map((highlight, index) => (
                                    <div key={index} className="col-12 md:col-4">
                                        <div className="text-center p-4">
                                            <div className="inline-flex align-items-center justify-content-center w-4rem h-4rem border-circle mb-4">
                                                <i className={`${highlight.icon} text-6xl text-cyan-500`}></i>
                                            </div>
                                            <h3 className="text-xl font-semibold text-900 mb-3">{highlight.title}</h3>
                                            <p className="text-600 line-height-3">{highlight.description}</p>
                                            <Badge value={highlight.severity === 'info' ? 'No aceptado' : highlight.severity === 'warning' ? 'Solo por defecto' : 'Plazos y evaluación'} severity={highlight.severity} className="mt-3" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Proceso de solicitud */}
                <div className="mb-6">
                    <Card>
                        <div className="p-0 text-center">
                            <h2 className="text-4xl font-bold text-primary mb-4">Proceso de solicitud</h2>
                            <p className="text-lg text-700 line-height-3">
                                Nuestro objetivo es brindarte una experiencia clara y segura. Sigue estos pasos:
                            </p>

                            <Steps 
                                model={processSteps} 
                                activeIndex={activeStep} 
                                onSelect={(e) => setActiveStep(e.index)}
                                readOnly={false}
                                className="mb-6"
                            />
                            
                            <div className="grid">
                                {processSteps.map((step, index) => (
                                    <div key={index} className="col-12 md:col-3">
                                        <div className={`surface-100 border-round p-4 h-full text-center transition-all transition-duration-300 cursor-pointer ${
                                            activeStep === index ? 'shadow-3 border-2 surface-border' : 'hover:shadow-2'
                                        }`} onClick={() => setActiveStep(index)}>
                                            <div className="inline-flex align-items-center justify-content-center w-3rem h-3rem border-circle mb-3">
                                                <i className={`${step.icon} text-6xl text-cyan-500`}></i>
                                            </div>
                                            <h4 className="text-lg font-semibold text-900 mb-2">{step.label}</h4>
                                            <p className="text-600 line-height-3 text-sm">{step.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                </div>                

                {/* Sección de contacto */}
                <Card className='mb-6'>
                    <div className="p-6">
                        <div className="grid align-items-center">
                            <div className="col-12 md:col-8 text-center md:text-left">
                                <h2 className="text-4xl font-bold text-900 mb-4">¿Necesitas iniciar una evaluación por defecto?</h2>
                                <p className="text-xl text-700 mb-6 line-height-3">
                                    Nuestro equipo técnico está listo para ayudarte con tu solicitud.
                                </p>
                            </div>
                            
                            {/* Imagen de soporte responsive */}
                            <div className="col-12 md:col-4">
                                <div className="w-full">
                                    <img 
                                        src="/imagenes/soporte.png" 
                                        alt="Soporte" 
                                        width="1312" 
                                        height="704"
                                        className="w-full h-auto border-round"
                                        style={{ maxWidth: '100%', height: 'auto' }}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>
                <Card className='mb-6'>
                    <div className="p-0 text-center">
                        {/* Información de tiempos */}
                        <i className="pi pi-info-circle text-blue-500 mr-2"></i>
                        <strong>Tiempo de respuesta:</strong> Respondemos todas las solicitudes dentro de las 24 horas hábiles.
                    </div>
                </Card>

                {/* Texto legal y condiciones completas */}
                <Card className='mb-6'>
                    <div className="p-6">
                        <h2 className="text-3xl font-bold text-primary mb-4">Texto legal y condiciones</h2>
                        <div className="text-800 line-height-3">
                            <h3 className="text-2xl font-semibold mb-3">1. Alcance y operador</h3>
                            <p>Estas políticas aplican a compras realizadas en Laptops.pe, operado por Digital Wins E.I.R.L, y están alineadas con la configuración de Google Merchant y el Código de Protección y Defensa del Consumidor (Ley N° 29571) del Perú.</p>

                            <h3 className="text-2xl font-semibold mb-3 mt-4">2. Devoluciones y cambios</h3>
                            <ul className="ml-3">
                                <li>Devoluciones: Aceptadas exclusivamente por productos defectuosos (defecto de fábrica), previa evaluación y dictamen del departamento técnico.</li>
                                <li>Cambios: No aceptados por preferencia, talla, color, compatibilidad u otros motivos no relacionados a defectos.</li>
                                <li>Reposición por defecto: Si se confirma el defecto, se ofrece reposición por el mismo modelo (sujeto a disponibilidad) o emisión de nota de crédito. No se realizan cambios por otro modelo.</li>
                            </ul>

                            <h3 className="text-2xl font-semibold mb-3 mt-4">3. Plazos y solicitud</h3>
                            <ul className="ml-3">
                                <li>La solicitud de evaluación técnica debe iniciarse dentro de los 7 días calendario posteriores a la entrega.</li>
                                <li>El dictamen técnico se emite dentro de un plazo máximo de 5 días hábiles desde la recepción del producto en nuestro centro de evaluación.</li>
                                <li>La coordinación de envíos o entregas para evaluación debe ser autorizada previamente por nuestro equipo de soporte.</li>
                            </ul>

                            <h3 className="text-2xl font-semibold mb-3 mt-4">4. Condiciones para aceptación</h3>
                            <ul className="ml-3">
                                <li>El producto debe incluir empaques originales, accesorios, manuales y número de serie legible.</li>
                                <li>No debe presentar señales de uso indebido, golpes, humedad, manipulación, instalación inadecuada o alteraciones no autorizadas.</li>
                                <li>La evaluación técnica puede requerir pruebas de software/diagnóstico; si el dictamen descarta defecto de fábrica, el producto será devuelto en las mismas condiciones.</li>
                            </ul>

                            <h3 className="text-2xl font-semibold mb-3 mt-4">5. Exclusiones</h3>
                            <ul className="ml-3">
                                <li>Daños por golpes, líquidos, sobrecarga eléctrica o uso fuera de especificación.</li>
                                <li>Incompatibilidades de software, configuraciones del usuario o desempeño esperado no garantizado por el fabricante.</li>
                                <li>Desgaste normal, variaciones cosméticas y fallas atribuibles a terceros.</li>
                            </ul>

                            <h3 className="text-2xl font-semibold mb-3 mt-4">6. Logística y costos</h3>
                            <ul className="ml-3">
                                <li>Si el defecto de fábrica es confirmado, los costos de retorno podrán ser asumidos por Laptops.pe/Digital Wins; de lo contrario, los costos de transporte y diagnóstico serán asumidos por el cliente.</li>
                                <li>Todo envío debe ser previamente autorizado y correctamente embalado para evitar daños durante el transporte.</li>
                            </ul>

                            <h3 className="text-2xl font-semibold mb-3 mt-4">7. Base legal y aceptación</h3>
                            <p>Estas políticas se interpretan conforme a la Ley N° 29571 (Código de Protección y Defensa del Consumidor). Al realizar una compra, el cliente declara haber leído y aceptado estas condiciones, que salvaguardan los intereses de Laptops.pe y Digital Wins.</p>
                        </div>
                    </div>
                </Card>

            </div>
            <Footer />
        </div></div>
    );
}